import { CommonModule } from '@angular/common';
import { Component, ContentChildren, Directive, EventEmitter, Input, OnChanges, OnDestroy, Output, QueryList, TemplateRef } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { Subscription } from 'rxjs';

/*
Contenedor para formularios públicos en pasos a los que se llega con un link de un solo uso (ver AF298).
Funciona como una máquina de estados: cada formulario le pasa la lista de pasos ([steps]) y qué hacer al enviar ([submit]).
Cada paso dice cómo se valida, qué hace al avanzar y a qué paso va según lo cargado (transiciones).
El contenedor se encarga del resto: los estados del link, la tarjeta, el "Paso X de Y", el texto del botón,
los errores por campo y el envío. Para sumar un paso: un objeto en [steps] y su <ng-template appFormStep="id" let-errors>.
Guía completa en "claude rules/docs/conocimiento.md" (Formularios públicos en pasos).
*/

export type PublicFormState = 'loading' | 'ok' | 'invalid' | 'expired' | 'used' | 'error' | 'done';

// destino especial de una transición: enviar el formulario
export const SUBMIT = '__submit__';

export interface PublicFormMessage {
    title: string;
    text: string;
}

export interface FormStepTransition {
    // id del paso siguiente, o SUBMIT
    to: string;
    // se toma la primera transición cuya condición se cumple (sin condición = siempre)
    when?: () => boolean;
}

export interface FormStepConfig {
    id: string;
    // errores por campo ({ campo: mensaje }); si hay alguno no se avanza
    validate?: () => Record<string, string>;
    // algo a hacer al avanzar, después de validar (opcional)
    onNext?: () => void | Promise<void>;
    transitions: FormStepTransition[];
    // texto del botón; por defecto "Continuar", o submitLabel si el paso siguiente es enviar
    nextLabel?: string;
}

// lo que devuelve [submit]: terminar, cambiar a un estado del link (vencido, ya usado...) o mostrar un error y seguir en el paso
export type PublicFormSubmitResult =
    | { state: 'done' | 'expired' | 'used' | 'invalid' }
    | { error: string };

// textos por defecto de los estados; cada formulario puede pisarlos con [messages]
const DEFAULT_MESSAGES: Record<Exclude<PublicFormState, 'ok' | 'loading'>, PublicFormMessage> = {
    invalid: {
        title: 'Enlace inválido',
        text: 'Revisá que el enlace sea el que te enviamos por WhatsApp, o pedí uno nuevo.',
    },
    expired: {
        title: 'El enlace venció',
        text: 'Por seguridad, los enlaces duran poco tiempo. Pedí uno nuevo por WhatsApp.',
    },
    used: {
        title: 'Este enlace ya se usó',
        text: 'Cada enlace sirve para un solo envío. Si necesitás hacer otro, pedí un enlace nuevo por WhatsApp.',
    },
    error: {
        title: 'No pudimos cargar el formulario',
        text: 'Probá de nuevo en unos minutos. Si sigue fallando, pedí un enlace nuevo por WhatsApp.',
    },
    done: {
        title: '¡Listo!',
        text: 'Recibimos tu formulario. Ya podés volver a WhatsApp.',
    },
};

@Directive({
    selector: 'ng-template[appFormStep]',
    standalone: true,
})
export class FormStepDirective {
    @Input('appFormStep') name!: string;
    constructor(public template: TemplateRef<unknown>) {}
}

/* Error de un campo, con el estilo de siempre: <app-field-error field="dni" [errors]="errors" /> */
@Component({
    selector: 'app-field-error',
    standalone: true,
    template: `@if (errors[field]) { <div class="errorValidation"><p>{{ errors[field] }}</p></div> }`,
})
export class FieldErrorComponent {
    @Input() field = '';
    @Input() errors: Record<string, string> = {};
}

@Component({
    selector: 'app-public-step-form',
    standalone: true,
    imports: [CommonModule, ButtonModule],
    templateUrl: './public-step-form.component.html',
    styleUrl: './public-step-form.component.scss',
})
export class PublicStepFormComponent implements OnChanges, OnDestroy {
    @Input() title = '';
    @Input() steps: FormStepConfig[] = [];
    @Input() submit?: () => Promise<PublicFormSubmitResult>;
    // formulario a escuchar: los errores ya mostrados se borran a medida que se corrigen
    @Input() set form(form: AbstractControl | undefined) {
        this.formSub?.unsubscribe();
        this.formSub = form?.valueChanges.subscribe(() => this.revalidate());
    }
    @Input() submitLabel = 'Enviar';
    @Input() messages: Partial<Record<PublicFormState, PublicFormMessage>> = {};
    // estado del link al cargar (lo decide el formulario); el envío lo puede cambiar (listo, vencido...)
    @Input() set state(state: PublicFormState) {
        this.currentState = state;
    }
    get state(): PublicFormState {
        return this.currentState;
    }
    @Output() stateChange = new EventEmitter<PublicFormState>();

    @ContentChildren(FormStepDirective) templates!: QueryList<FormStepDirective>;

    currentState: PublicFormState = 'loading';
    current = '';
    errors: Record<string, string> = {};
    errorText = '';
    busy = false;
    // pila de pasos hechos: "Atrás" vuelve por el mismo camino aunque los pasos cambien según lo elegido
    private stack: string[] = [];
    private formSub?: Subscription;

    get step(): FormStepConfig | undefined {
        return this.steps.find(s => s.id === this.current);
    }

    get currentTemplate(): TemplateRef<unknown> | null {
        return this.templates?.find(t => t.name === this.current)?.template ?? null;
    }

    get stepNumber(): number {
        return this.stack.length + 1;
    }

    // pasos del camino actual: los hechos + el actual + los que faltan según lo cargado
    get totalSteps(): number {
        let count = this.stack.length + 1;
        let id = this.nextTarget(this.step);
        const visited = new Set([this.current]);
        while (id && id !== SUBMIT && !visited.has(id)) {
            visited.add(id);
            count++;
            id = this.nextTarget(this.steps.find(s => s.id === id));
        }
        return count;
    }

    get nextLabel(): string {
        return this.step?.nextLabel ?? (this.nextTarget(this.step) === SUBMIT ? this.submitLabel : 'Continuar');
    }

    get canGoBack(): boolean {
        return this.stack.length > 0;
    }

    get message(): PublicFormMessage | null {
        if (this.currentState === 'ok' || this.currentState === 'loading') {
            return null;
        }
        return this.messages[this.currentState] ?? DEFAULT_MESSAGES[this.currentState];
    }

    async next() {
        const step = this.step;
        if (!step || this.busy) {
            return;
        }
        this.errorText = '';
        this.errors = step.validate?.() ?? {};
        if (Object.keys(this.errors).length) {
            return;
        }
        await step.onNext?.();
        const target = this.nextTarget(step);
        if (!target) {
            console.error(`[public-step-form] el paso "${step.id}" no tiene una transición que se cumpla`);
            return;
        }
        if (target === SUBMIT) {
            await this.runSubmit();
            return;
        }
        this.stack.push(this.current);
        this.current = target;
    }

    back() {
        const previous = this.stack.pop();
        if (previous) {
            this.current = previous;
            this.errors = {};
            this.errorText = '';
        }
    }

    ngOnChanges() {
        // el primer paso es el primero de la lista (no depende del orden en que llegan los inputs)
        if (!this.current && this.steps.length) {
            this.current = this.steps[0].id;
        }
    }

    ngOnDestroy() {
        this.formSub?.unsubscribe();
    }

    private nextTarget(step: FormStepConfig | undefined): string | undefined {
        return step?.transitions.find(t => !t.when || t.when())?.to;
    }

    private revalidate() {
        if (!Object.keys(this.errors).length) {
            return;
        }
        const current = this.step?.validate?.() ?? {};
        this.errors = Object.fromEntries(Object.keys(this.errors).filter(k => current[k]).map(k => [k, current[k]]));
    }

    private async runSubmit() {
        if (!this.submit) {
            console.error('[public-step-form] falta [submit]');
            return;
        }
        this.busy = true;
        try {
            const result = await this.submit();
            if ('error' in result) {
                this.errorText = result.error;
                return;
            }
            this.currentState = result.state;
            this.stateChange.emit(result.state);
        } catch (error) {
            console.error('[public-step-form] error al enviar', error);
            this.errorText = 'No pudimos enviar el formulario. Probá de nuevo en unos minutos.';
        } finally {
            this.busy = false;
        }
    }
}
